<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { api, extractError } from '@/api/client'
import { useUserStore } from '@/stores/user'
import { useKeysStore } from '@/stores/keys'
import QrModal from '@/components/keys/QrModal.vue'
import Modal from '@/components/ui/Modal.vue'
import { showMessage } from '@/utils/toast'
import { buildExplicitFormatLinks } from '@/utils/clientLinks'
import { protocolFamily } from '@/utils/keys'
import type { UserSettings } from '@/types'

const user = useUserStore()
const keysStore = useKeysStore()

const link = ref('')
const loading = ref(false)
const error = ref('')
const copied = ref(false)
const qrOpen = ref(false)
const infoOpen = ref(false)
const confirmRegenerateOpen = ref(false)
const formatsOpen = ref(false)
const excludeXhttp = ref(false)
const excludeXtls = ref(false)
const excludeVless = ref(false)
const excludeAnytls = ref(false)
const excludeTuic = ref(false)
const excludeSsh = ref(false)

const hasLink = computed(() => link.value.length > 0)
const formatLinks = computed(() => buildExplicitFormatLinks(link.value))

const presentProtocols = computed(() => {
  const families = new Set(
    keysStore.items
      .filter((k) => !k.is_expired)
      .map((k) => protocolFamily(k.protocol_type)),
  )
  return {
    vless: families.has('vless'),
    anytls: families.has('anytls'),
    tuic: families.has('tuic'),
    ssh: families.has('ssh'),
  }
})

const hasProtocolFilters = computed(
  () =>
    presentProtocols.value.vless
    || presentProtocols.value.anytls
    || presentProtocols.value.tuic
    || presentProtocols.value.ssh,
)

const LS_XHTTP = 'excludeXhttpCheckbox'
const LS_XTLS = 'excludeXtlsVisionCheckbox'

function currentFilters() {
  return {
    exclude_xhttp: excludeXhttp.value,
    exclude_xtls_vision: excludeXtls.value,
    exclude_vless: excludeVless.value,
    exclude_anytls: excludeAnytls.value,
    exclude_tuic: excludeTuic.value,
    exclude_ssh: excludeSsh.value,
  }
}

function applyFiltersToState(filters: {
  exclude_xhttp?: boolean
  exclude_xtls_vision?: boolean
  exclude_vless?: boolean
  exclude_anytls?: boolean
  exclude_tuic?: boolean
  exclude_ssh?: boolean
}) {
  excludeXhttp.value = !!filters.exclude_xhttp
  excludeXtls.value = !!filters.exclude_xtls_vision
  excludeVless.value = !!filters.exclude_vless
  excludeAnytls.value = !!filters.exclude_anytls
  excludeTuic.value = !!filters.exclude_tuic
  excludeSsh.value = !!filters.exclude_ssh
}

function linkFromProfile(): string {
  const code = user.profile?.subscription_link_code
  if (!code) return ''
  return `${window.location.origin}/sublink/${code}`
}

function applyLinkFromResponse(fullUrl?: string, path?: string) {
  if (fullUrl) {
    link.value = fullUrl
  } else if (path) {
    link.value = `${window.location.origin}${path}`
  }
  const match = link.value.match(/\/sublink\/([A-Za-z0-9]{70})/)
  if (match && user.profile) {
    user.profile.subscription_link_code = match[1]
  }
}

async function loadFilters() {
  try {
    const { data } = await api.get<{ success: boolean; settings?: UserSettings }>(
      '/api/user/settings',
    )
    if (data.success && data.settings?.subscription_filters) {
      applyFiltersToState(data.settings.subscription_filters)
      user.updateSubscriptionFilters(currentFilters())
      return
    }
    throw new Error('no filters')
  } catch {
    excludeXhttp.value = localStorage.getItem(LS_XHTTP) === '1'
    excludeXtls.value = localStorage.getItem(LS_XTLS) === '1'
  }
}

async function saveFilters(): Promise<{ ok: boolean; message?: string }> {
  localStorage.setItem(LS_XHTTP, excludeXhttp.value ? '1' : '0')
  localStorage.setItem(LS_XTLS, excludeXtls.value ? '1' : '0')
  const filters = currentFilters()
  try {
    const { data } = await api.post<{ success: boolean; message?: string }>('/api/user/settings', {
      subscription_filters: filters,
    })
    if (!data.success) return { ok: false, message: data.message }
    user.updateSubscriptionFilters(filters)
    return { ok: true }
  } catch (e) {
    return { ok: false, message: extractError(e) }
  }
}

type FilterKey = 'xhttp' | 'xtls' | 'vless' | 'anytls' | 'tuic' | 'ssh'

async function onFilterChange(which: FilterKey) {
  const res = await saveFilters()
  if (res.ok) {
    showMessage('Настройки сохранены', 'bottom-end')
    return
  }
  switch (which) {
    case 'xhttp':
      excludeXhttp.value = !excludeXhttp.value
      break
    case 'xtls':
      excludeXtls.value = !excludeXtls.value
      break
    case 'vless':
      excludeVless.value = !excludeVless.value
      break
    case 'anytls':
      excludeAnytls.value = !excludeAnytls.value
      break
    case 'tuic':
      excludeTuic.value = !excludeTuic.value
      break
    case 'ssh':
      excludeSsh.value = !excludeSsh.value
      break
  }
  showMessage(res.message || 'Не удалось сохранить настройки', 'bottom-end')
}

async function generateLink(regenerate = false) {
  loading.value = true
  error.value = ''
  try {
    const { data } = await api.post<{
      success: boolean
      full_url?: string
      link?: string
      message?: string
      regenerated?: boolean
    }>('/api/user/subscription-link', regenerate ? { regenerate: true } : {})
    if (!data.success) {
      error.value = data.message || 'Не удалось создать ссылку'
      return
    }
    applyLinkFromResponse(data.full_url, data.link)
    if (regenerate) {
      showMessage('Новая ссылка создана. Обновите её в клиенте.', 'bottom-end')
    }
    await saveFilters()
  } catch (e) {
    error.value = extractError(e)
  } finally {
    loading.value = false
  }
}

function openRegenerateConfirm() {
  confirmRegenerateOpen.value = true
}

async function confirmRegenerate() {
  confirmRegenerateOpen.value = false
  await generateLink(true)
}

async function copyLink() {
  if (!link.value) return
  try {
    await navigator.clipboard.writeText(link.value)
    copied.value = true
    setTimeout(() => (copied.value = false), 1500)
  } catch {
    error.value = 'Не удалось скопировать'
  }
}

async function copyText(value: string) {
  if (!value) return
  try {
    await navigator.clipboard.writeText(value)
    showMessage('Скопировано', 'bottom-end')
  } catch {
    error.value = 'Не удалось скопировать'
  }
}

watch(
  () => user.profile?.subscription_link_code,
  () => {
    if (!link.value) {
      link.value = linkFromProfile()
    }
  },
)

onMounted(async () => {
  link.value = linkFromProfile()
  await loadFilters()
})
</script>

<template>
  <div class="panel mb-4 overflow-hidden !p-0">
    <div class="panel-header">
      <h2 class="flex items-center gap-2 text-base font-bold">
        Единая ссылка доступа на все ключи
        <button
          type="button"
          class="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-white-dark/50 text-xs font-bold text-white-dark transition hover:border-primary hover:text-primary"
          aria-label="Справка о ссылке подписки"
          title="Справка"
          @click="infoOpen = true"
        >
          ?
        </button>
      </h2>
    </div>

    <div class="panel-body">
      <div v-if="error" class="alert alert-danger">{{ error }}</div>

      <div v-if="hasLink" class="space-y-4">
        <div class="flex flex-col gap-2 sm:flex-row">
          <input
            :value="link"
            readonly
            class="form-input min-w-0 flex-1 font-mono text-xs"
            @focus="($event.target as HTMLInputElement).select()"
          />
          <div class="flex shrink-0 gap-2">
            <button type="button" class="btn btn-sm btn-outline-primary" @click="copyLink">
              {{ copied ? 'Скопировано' : 'Копировать' }}
            </button>
            <button type="button" class="btn btn-sm btn-outline-primary" @click="qrOpen = true">
              QR
            </button>
            <button
              type="button"
              class="btn btn-sm btn-outline-warning"
              :disabled="loading"
              @click="openRegenerateConfirm"
            >
              Сменить адрес
            </button>
          </div>
        </div>

        <div class="rounded-md border border-white-light/40 bg-white-light/5 p-3">
          <div class="mb-2 text-sm font-semibold">Фильтры подписки</div>
          <p class="mb-3 text-xs text-muted">
            Формат ответа подбирается по приложению автоматически.
            Клиенты на ядрах sing-box и mihomo получают полный профиль вместе с SSH,
            остальные — только URI-ключи. Исключённые протоколы не попадают в ссылку,
            тумблеры соответствующих ключей блокируются.
          </p>

          <div v-if="hasProtocolFilters" class="mb-3 space-y-2">
            <label
              v-if="presentProtocols.vless"
              class="flex cursor-pointer items-start gap-2"
            >
              <input
                v-model="excludeVless"
                type="checkbox"
                class="mt-0.5 h-4 w-4 shrink-0 rounded border-white-light text-primary"
                @change="onFilterChange('vless')"
              />
              <span class="text-sm font-semibold">Исключить vless</span>
            </label>
            <label
              v-if="presentProtocols.anytls"
              class="flex cursor-pointer items-start gap-2"
            >
              <input
                v-model="excludeAnytls"
                type="checkbox"
                class="mt-0.5 h-4 w-4 shrink-0 rounded border-white-light text-primary"
                @change="onFilterChange('anytls')"
              />
              <span class="text-sm font-semibold">Исключить anytls</span>
            </label>
            <label
              v-if="presentProtocols.tuic"
              class="flex cursor-pointer items-start gap-2"
            >
              <input
                v-model="excludeTuic"
                type="checkbox"
                class="mt-0.5 h-4 w-4 shrink-0 rounded border-white-light text-primary"
                @change="onFilterChange('tuic')"
              />
              <span class="text-sm font-semibold">Исключить tuic</span>
            </label>
            <label
              v-if="presentProtocols.ssh"
              class="flex cursor-pointer items-start gap-2"
            >
              <input
                v-model="excludeSsh"
                type="checkbox"
                class="mt-0.5 h-4 w-4 shrink-0 rounded border-white-light text-primary"
                @change="onFilterChange('ssh')"
              />
              <span class="text-sm font-semibold">Исключить ssh</span>
            </label>
          </div>

          <button
            type="button"
            class="text-xs font-semibold text-primary hover:underline"
            @click="formatsOpen = !formatsOpen"
          >
            {{ formatsOpen ? 'Скрыть' : 'Показать' }} ссылки с явным форматом
          </button>

          <div v-if="formatsOpen" class="mt-2 space-y-2">
            <p class="text-xs text-muted">
              Нужны, если приложение не сообщает себя в User-Agent или определилось неверно.
            </p>
            <div
              v-for="item in formatLinks"
              :key="item.title"
              class="flex flex-col gap-1 rounded border border-white-light/30 p-2 sm:flex-row sm:items-center sm:gap-2"
            >
              <div class="min-w-0 flex-1">
                <div class="text-xs font-semibold">{{ item.title }}</div>
                <div class="truncate font-mono text-[10px] text-muted">{{ item.href }}</div>
                <div class="text-[10px] text-muted">{{ item.hint }}</div>
              </div>
              <button
                type="button"
                class="btn btn-sm btn-outline-primary shrink-0"
                @click="copyText(item.href)"
              >
                Копировать
              </button>
            </div>
          </div>
        </div>

        <label class="flex cursor-pointer items-start gap-2">
          <input
            v-model="excludeXhttp"
            type="checkbox"
            class="mt-0.5 h-4 w-4 shrink-0 rounded border-white-light text-primary"
            @change="onFilterChange('xhttp')"
          />
          <span>
            <span class="text-sm font-semibold">Исключить ключи с транспортом xhttp</span>
            <span class="mt-0.5 block text-xs text-muted">
              Актуально, если клиентское приложение не поддерживает данный транспорт
            </span>
          </span>
        </label>

        <label class="flex cursor-pointer items-start gap-2">
          <input
            v-model="excludeXtls"
            type="checkbox"
            class="mt-0.5 h-4 w-4 shrink-0 rounded border-white-light text-primary"
            @change="onFilterChange('xtls')"
          />
          <span>
            <span class="text-sm font-semibold">Исключить ключи с XTLS Vision (xtls-rprx-vision)</span>
            <span class="mt-0.5 block text-xs text-muted">
              Актуально, если в клиентском приложении включён Mux (мультиплексирование)
            </span>
          </span>
        </label>

      </div>

      <button
        v-else
        type="button"
        class="btn btn-primary"
        :disabled="loading"
        @click="generateLink(false)"
      >
        {{ loading ? 'Создание…' : 'Создать ссылку подписки' }}
      </button>
    </div>

    <QrModal :open="qrOpen" :data="link" title="Subscription Link QR" @close="qrOpen = false" />

    <Modal
      :open="infoOpen"
      title="Единая ссылка доступа на все ключи"
      max-width="lg"
      @close="infoOpen = false"
    >
      <div class="space-y-4 text-sm leading-relaxed text-[#1f2937]">
        <p>
          Данная ссылка позволяет одним действием экспортировать все ваши ключи в клиентское приложение.
          При этом клиент должен поддерживать систему подписок.
        </p>
        <p>
          Тумблеры у ключей исключают конкретные протоколы из ссылки; чекбоксы ниже - массово по типу транспорта
          или семейству протокола. В подписку попадают только ключи, прошедшие оба правила.
        </p>
        <p>
          Формат ответа подбирается по приложению: клиенты на ядре sing-box (SFA/SFI/SFM, Hiddify,
          Karing, NekoBox) получают готовый sing-box профиль, клиенты на ядре mihomo (FlClash,
          Clash Verge Rev, Mihomo Party, CMFA) — конфиг Clash. Только в этих двух форматах может
          передаваться SSH: у SSH нет вида ссылки вроде vless://, поэтому в обычную подписку
          v2rayN/Streisand/Shadowrocket он не попадёт ни при каких настройках.
        </p>
        <p>
          Ключи с транспортом xhttp в профили sing-box и Clash не попадают — этот транспорт есть
          только в Xray, оба ядра его не реализуют. В обычной URI-подписке они остаются.
        </p>
        <p>
          Так же вы можете изменить адрес ссылки, если считаете, что она скомпрометирована через кнопку
          «Сменить адрес».
        </p>
      </div>

      <template #footer>
        <button type="button" class="btn btn-primary" @click="infoOpen = false">
          Закрыть
        </button>
      </template>
    </Modal>

    <Modal
      :open="confirmRegenerateOpen"
      title="Сменить адрес ссылки?"
      max-width="md"
      @close="confirmRegenerateOpen = false"
    >
      <div class="space-y-3 text-sm leading-relaxed text-[#1f2937]">
        <p>
          Будет создан новый адрес подписки. Старый перестанет работать.
        </p>
        <p>
          Обязательно замените ссылку в клиентском приложении — иначе изменения в ключах
          (новые серверы, отключённые протоколы, обновлённые конфиги) не будут подгружаться.
        </p>
      </div>

      <template #footer>
        <button
          type="button"
          class="btn btn-outline-secondary"
          :disabled="loading"
          @click="confirmRegenerateOpen = false"
        >
          Отмена
        </button>
        <button
          type="button"
          class="btn btn-warning"
          :disabled="loading"
          @click="confirmRegenerate"
        >
          {{ loading ? 'Смена…' : 'Да, сменить адрес' }}
        </button>
      </template>
    </Modal>
  </div>
</template>
